import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Player } from '../../types/player';

interface AttributeComparisonProps {
  player1: Player;
  player2: Player;
}

const AttributeComparison: React.FC<AttributeComparisonProps> = ({ player1, player2 }) => {
  const { t } = useTranslation();

  const getAttribute = (player: Player, key: string): number => {
    const value = (player as any)[key];
    return typeof value === 'number' ? value : 0;
  };

  const getPercentageBar = (value1: number, value2: number) => {
    const total = value1 + value2 || 1;
    const percentage1 = (value1 / total) * 100;
    const percentage2 = (value2 / total) * 100;

    return (
      <div className="flex items-center space-x-2">
        <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden flex">
          <div
            className="bg-blue-500 flex items-center justify-end pr-2"
            style={{ width: `${percentage1}%` }}
          >
            {value1 > 0 && (
              <span className="text-white text-xs font-bold">{value1.toFixed(2)}</span>
            )}
          </div>
          <div
            className="bg-red-500 flex items-center justify-start pl-2"
            style={{ width: `${percentage2}%` }}
          >
            {value2 > 0 && (
              <span className="text-white text-xs font-bold">{value2.toFixed(2)}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const comparisonAttributes = [
    {
      category: t('comparison.performance'),
      items: [
        { label: t('comparison.goals'), key1: 'goles', key2: 'goles' },
        { label: t('comparison.assists'), key1: 'asistencias', key2: 'asistencias' },
        { label: t('comparison.goalsP90'), key1: 'goles_por_90', key2: 'goles_por_90' },
        { label: t('comparison.assistsP90'), key1: 'asistencias_por_90', key2: 'asistencias_por_90' },
        { label: t('comparison.matchesPlayed'), key1: 'partidos_jugados', key2: 'partidos_jugados' },
        { label: t('comparison.minutesPlayed'), key1: 'minutos_jugados', key2: 'minutos_jugados' }
      ]
    },
    {
      category: t('comparison.technical'),
      items: [
        { label: 'xG', key1: 'xg_esperado', key2: 'xg_esperado' },
        { label: 'npxG', key1: 'npxg_esperado', key2: 'npxg_esperado' },
        { label: 'xA', key1: 'xag_esperado', key2: 'xag_esperado' },
        { label: 'xG/90', key1: 'xg_por_90', key2: 'xg_por_90' },
        { label: 'xA/90', key1: 'xag_por_90', key2: 'xag_por_90' }
      ]
    },
    {
      category: t('comparison.progressions'),
      items: [
        { label: t('comparison.progressions') + ' (Total)', key1: 'progresion_conduccion', key2: 'progresion_conduccion', combine: ['progresion_pase', 'progresion_regate'] },
        { label: 'Progresión Conducción', key1: 'progresion_conduccion', key2: 'progresion_conduccion' },
        { label: 'Progresión Pase', key1: 'progresion_pase', key2: 'progresion_pase' },
        { label: 'Progresión Regate', key1: 'progresion_regate', key2: 'progresion_regate' }
      ]
    },
    {
      category: 'Disciplina',
      items: [
        { label: t('comparison.yellowCards'), key1: 'tarjetas_amarillas', key2: 'tarjetas_amarillas' },
        { label: t('comparison.redCards'), key1: 'tarjetas_rojas', key2: 'tarjetas_rojas' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {comparisonAttributes.map((category, categoryIndex) => (
        <div key={categoryIndex} className="space-y-4">
          <h3 className="text-lg font-bold text-gray-800 border-b-2 border-gray-200 pb-2">
            {category.category}
          </h3>
          <div className="space-y-3">
            {category.items.map((item, itemIndex) => {
              let value1 = getAttribute(player1, item.key1);
              let value2 = getAttribute(player2, item.key2);

              // If combine is specified, add those values
              if (item.combine) {
                item.combine.forEach(key => {
                  value1 += getAttribute(player1, key);
                  value2 += getAttribute(player2, key);
                });
              }

              return (
                <div key={itemIndex}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  </div>
                  {getPercentageBar(value1, value2)}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AttributeComparison;
